<template>
  <div class="cc-commissions-tax-container">
    <div class="cc-header">
      <span class="cc-header-title">{{ t('consultantCorner.MyDocuments') }}</span>
    </div>

    <kTabStrip @select="onTabSelect" :selected="selectedTab" class="cc-tabstrip">
      <TabStripTab :title="t('consultantCorner.CommissionStatements')">
        <div class="cc-tab-content">
          <div class="cc-content-wrapper">
            <div class="cc-controls-row">
              <div class="cc-search-container">
                <input
                  v-model="searchTerm"
                  type="text"
                  :placeholder="t('consultantCorner.SearchByDocumentTitle')"
                  class="cc-search-input"
                  @input="filterDocuments"
                />
              </div>
              <div class="cc-filter-container">
                <DropDownList
                  :data-items="dateFilterOptions"
                  :value="selectedDateFilter"
                  @change="onDateFilterChange"
                  :textField="'text'"
                  :valueField="'value'"
                  :valuePrimitive="true"
                  class="cc-dropdown"
                />
              </div>
            </div>

            <div class="cc-documents-list">
            <div v-if="loading" class="cc-loading">
              {{ t('labels.AutoCompleteLoading') }}
            </div>
            <div v-else-if="filteredCommissionDocuments.length === 0" class="cc-no-results">
              {{ t('consultantCorner.NoCommissionDocumentsFound') }}
            </div>
            <div v-else>
              <div v-for="yearGroup in groupedCommissionDocuments" :key="yearGroup.year" class="cc-year-group">
                <div class="cc-year-header">{{ yearGroup.year }}</div>
                <div v-for="document in yearGroup.documents" :key="document.beeDocumentGuid" class="cc-document-row">
                  <div class="cc-document-info">
                    <div class="cc-document-date">{{ formatDateEU(document.createdDate) }}</div>
                    <div class="cc-document-name">{{ document.documentName }}</div>
                  </div>
                  <div class="cc-document-actions">
                    <KButton @click="viewDocument(document)" class="cc-view-button">{{ t('labels.View') }}</KButton>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </TabStripTab>

      <TabStripTab :title="t('consultantCorner.TaxDocuments')">
        <div class="cc-tab-content">
          <div class="cc-content-wrapper">
            <div class="cc-controls-row">
              <div class="cc-search-container">
                <input
                  v-model="taxSearchTerm"
                  type="text"
                  :placeholder="t('consultantCorner.SearchByDocumentTitle')"
                  class="cc-search-input"
                  @input="filterTaxDocuments"
                />
              </div>
              <div class="cc-filter-container">
                <DropDownList
                  :data-items="dateFilterOptions"
                  :value="selectedTaxDateFilter"
                  @change="onTaxDateFilterChange"
                  :textField="'text'"
                  :valueField="'value'"
                  :valuePrimitive="true"
                  class="cc-dropdown"
                />
              </div>
            </div>

            <div class="cc-documents-list">
            <div v-if="loading" class="cc-loading">
              {{ t('labels.AutoCompleteLoading') }}
            </div>
            <div v-else-if="filteredTaxDocuments.length === 0" class="cc-no-results">
              {{ t('consultantCorner.NoTaxDocumentsFound') }}
            </div>
            <div v-else>
              <div v-for="yearGroup in groupedTaxDocuments" :key="yearGroup.year" class="cc-year-group">
                <div class="cc-year-header">{{ yearGroup.year }}</div>
                <div v-for="document in yearGroup.documents" :key="document.beeDocumentGuid" class="cc-document-row">
                  <div class="cc-document-info">
                    <div class="cc-document-date">{{ formatDateEU(document.createdDate) }}</div>
                    <div class="cc-document-name">{{ document.documentName }}</div>
                  </div>
                  <div class="cc-document-actions">
                    <KButton @click="viewDocument(document)" class="cc-view-button">{{ t('labels.View') }}</KButton>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </TabStripTab>
    </kTabStrip>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { TabStrip as kTabStrip, TabStripTab } from '@progress/kendo-vue-layout'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { DropDownList } from '@progress/kendo-vue-dropdowns'
import { GetBusinessEntityDocuments } from '../../api/ConsultantCorner'
import { GetBearerToken, GetBeeNumberAuthenicatedUser, GetApiKey, GetWebConnectorToken, GetCultureName } from '../../stores/SettingsStore'
import { handleError } from '../../services/ErrorService'
import Swal from 'sweetalert2'
import type { IDocument } from '../../types/ConsultantCornerTypes'

const { t } = useI18n()
const logger = useLogger()

const COMMISSION_CATEGORY_ID = ref('COMMISSIONS')
const TAX_CATEGORY_ID = ref('TAXES')

const documents = ref<IDocument[]>([])
const searchTerm = ref('')
const taxSearchTerm = ref('')
const selectedTab = ref(0)
const selectedDateFilter = ref('all')
const selectedTaxDateFilter = ref('all')
const loading = ref(false)

const dateFilterOptions = [
  { text: t('consultantCorner.Last3Months'), value: '3months' },
  { text: t('consultantCorner.Last6Months'), value: '6months' },
  { text: t('consultantCorner.Last12Months'), value: '12months' },
  { text: t('consultantCorner.Last24Months'), value: '24months' },
  { text: t('consultantCorner.AllTime'), value: 'all' }
]

const commissionDocuments = computed(() =>
  documents.value.filter(doc => doc.documentCategory.categoryId === COMMISSION_CATEGORY_ID.value)
)

const taxDocuments = computed(() =>
  documents.value.filter(doc => doc.documentCategory.categoryId === TAX_CATEGORY_ID.value)
)

const filteredCommissionDocuments = computed(() => {
  let filtered = commissionDocuments.value

  if (searchTerm.value) {
    filtered = filtered.filter(doc =>
      doc.documentName.toLowerCase().includes(searchTerm.value.toLowerCase())
    )
  }

  if (selectedDateFilter.value && selectedDateFilter.value !== 'all') {
    const monthsMap: Record<string, number> = { '3months': 3, '6months': 6, '12months': 12, '24months': 24 }
    const months = monthsMap[selectedDateFilter.value]
    if (months) {
      const cutoffDate = new Date()
      cutoffDate.setMonth(cutoffDate.getMonth() - months)
      filtered = filtered.filter(doc => {
        const docDate = new Date(doc.createdDate)
        return docDate >= cutoffDate
      })
    }
  }

  return filtered
})

const filteredTaxDocuments = computed(() => {
  let filtered = taxDocuments.value

  if (taxSearchTerm.value) {
    filtered = filtered.filter(doc =>
      doc.documentName.toLowerCase().includes(taxSearchTerm.value.toLowerCase())
    )
  }

  if (selectedTaxDateFilter.value && selectedTaxDateFilter.value !== 'all') {
    const monthsMap: Record<string, number> = { '3months': 3, '6months': 6, '12months': 12, '24months': 24 }
    const months = monthsMap[selectedTaxDateFilter.value]
    if (months) {
      const cutoffDate = new Date()
      cutoffDate.setMonth(cutoffDate.getMonth() - months)
      filtered = filtered.filter(doc => {
        const docDate = new Date(doc.createdDate)
        return docDate >= cutoffDate
      })
    }
  }

  filtered = filtered.sort((a, b) =>
    new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
  )

  return filtered
})

const groupedCommissionDocuments = computed(() => {
  const groups = filteredCommissionDocuments.value.reduce((acc, doc) => {
    const year = new Date(doc.createdDate).getFullYear()
    if (!acc[year]) acc[year] = []
    acc[year].push(doc)
    return acc
  }, {} as Record<number, IDocument[]>)

  const currentYear = new Date().getFullYear()
  const sortedYears = Object.keys(groups)
    .map(Number)
    .sort((a, b) => {
      if (a === currentYear) return -1
      if (b === currentYear) return 1
      return b - a
    })

  return sortedYears.map(year => ({
    year,
    documents: groups[year].sort((a, b) =>
      new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
    )
  }))
})

const groupedTaxDocuments = computed(() => {
  const groups = filteredTaxDocuments.value.reduce((acc, doc) => {
    const year = new Date(doc.createdDate).getFullYear()
    if (!acc[year]) acc[year] = []
    acc[year].push(doc)
    return acc
  }, {} as Record<number, IDocument[]>)

  const currentYear = new Date().getFullYear()
  const sortedYears = Object.keys(groups)
    .map(Number)
    .sort((a, b) => {
      if (a === currentYear) return -1
      if (b === currentYear) return 1
      return b - a
    })

  return sortedYears.map(year => ({
    year,
    documents: groups[year].sort((a, b) =>
      new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
    )
  }))
})

const formatDateEU = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  })
}

const viewDocument = async (doc: IDocument) => {
  try {
    const [apiKey, bearerToken, webConnectorToken, cultureName] = await Promise.all([
      GetApiKey(),
      GetBearerToken(),
      GetWebConnectorToken(),
      GetCultureName()
    ])

    const headers: Record<string, string> = {
      'x-api-key': apiKey,
      'Authorization': `Bearer ${bearerToken}`,
      'x-WebConnector-Token': webConnectorToken,
      'Accept': 'application/pdf'
    }

    if (cultureName) {
      headers['Accept-Language'] = cultureName
    }

    const xhr = new XMLHttpRequest()
    xhr.open('GET', doc.url, true)
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value)
    })
    xhr.responseType = 'blob'

    xhr.onload = async function() {
      if (xhr.status === 200) {
        const blob = new Blob([xhr.response], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        window.open(url, '_blank')
      } else {
        // Check if response is JSON with ExpiredUrlException
        try {
          const text = await new Blob([xhr.response]).text()
          const errorResponse = JSON.parse(text)
          if (errorResponse?.responseStatus?.errorCode === 'ExpiredUrlException') {
            logger.info('Document URL expired')
            Swal.fire({
              text: t('consultantCorner.DocumentUrlExpired'),
              icon: 'error'
            })
            return
          }
        } catch {
          // Not JSON or parsing failed, continue with normal error handling
        }
        logger.error('Error accessing document:', xhr.status)
        Swal.fire({
          text: `Error accessing document: ${xhr.status}`,
          icon: 'error'
        })
      }
    }

    xhr.onerror = function() {
      logger.error('Network error occurred while accessing document')
      void handleError(new Error('Network error occurred while accessing document'), t)
    }

    xhr.send()
  } catch (error) {
    logger.error('Error opening document:', error)
    await handleError(error, t)
  }
}

const onTabSelect = (...args: any[]) => {
  const event = args[0]
  selectedTab.value = event.selected
}

const onDateFilterChange = (event: any) => {
  console.log('onDateFilterChange', event)
  selectedDateFilter.value = event.target.value
}

const onTaxDateFilterChange = (event: any) => {
  console.log('onTaxDateFilterChange', event)
  selectedTaxDateFilter.value = event.target.value
}

const filterDocuments = () => {
}

const filterTaxDocuments = () => {
}

onMounted(async () => {
  try {
    loading.value = true
    const beeNumber = await GetBeeNumberAuthenicatedUser()
    if (beeNumber) {
      const response = await GetBusinessEntityDocuments(beeNumber)
      console.log('response', response)
      documents.value = response.items.sort((a, b) =>
        new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
      )
    }
  } catch (error) {
    logger.error('Error loading documents:', error)
    await handleError(error, t)
  } finally {
    loading.value = false
  }
})
</script>

<style lang="scss" scoped>
/* Estilos principales del contenedor */
.cc-commissions-tax-container {
  width: 100%;
  max-width: 100%;
  color: #333;
  padding: $spacing-5 $spacing-5 $spacing-1;
  background-color: var(--vt-c-white);
  border-radius: $radius-md;
  box-shadow: 0 10px 15px rgba(black, 0.1);
  display: flex;
  flex-direction: column;
}

.cc-tabstrip {
  width: 100%;
  display: flex;
  flex-direction: column;
}

/* Estilos del encabezado */
.cc-header {
  background-color: var(--vt-c-teal-dark);
  color: white;
  padding: $spacing-4 $spacing-5;
  margin-bottom: 20px;
  border-radius: $radius-lg;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cc-header-title {
  @include typography-body;
  @include font-weight-semibold;
  letter-spacing: 0.5px;
}

/* Estilos de las pestañas */
:deep(.k-tabstrip) {
  border: none;
  background: transparent;
  width: 100%;
}

:deep(.k-tabstrip-items-wrapper) {
  background-color: var(--vt-c-white-soft);
  border-radius: $radius-md $radius-md 0 0;
  border-bottom: 2px solid var(--vt-c-teal-dark);
}

:deep(.k-tabstrip-item) {
  @include font-weight-medium;
  border: none;
  color: #666;
  padding: $spacing-3 $spacing-5;
  transition: all 0.2s ease;
}

:deep(.k-tabstrip-item.k-active) {
  @include font-weight-semibold;
  color: var(--vt-c-teal-dark);
  border-bottom: 2px solid var(--vt-c-teal-dark);
}

:deep(.k-tabstrip-content) {
  border: 1px solid #e0e0e0;
  border-top: none;
  border-radius: 0 0 $radius-sm $radius-sm;
  background-color: var(--vt-c-white);
  padding: 0;
  width: 100%;
  display: flex;
  justify-content: center;
}

/* Estilos del contenido de las pestañas */
.cc-tab-content {
  padding: $card-padding;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  flex: 1;
}

.cc-content-wrapper {
  width: 100%;
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto;
}

/* Fila de controles (búsqueda y filtro) */
.cc-controls-row {
  display: flex;
  flex-direction: column;
  gap: $spacing-4;
  margin: 0 auto 25px;
  width: 100%;
  max-width: 1000px;
  padding: 0;
  box-sizing: border-box;
  align-items: center;
}

@media (min-width: 768px) {
  .cc-controls-row {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: $spacing-5;
  }

  .cc-filter-container {
    min-width: 200px;
    max-width: 250px;
  }

  .cc-document-actions {
    margin-left: 15px;
  }
}

/* Contenedor de búsqueda */
.cc-search-container {
  flex: 1;
  width: 100%;
  display: flex;
  align-items: center;
}

/* Contenedor de filtro */
.cc-filter-container {
  min-width: 200px;
  width: 100%;
  display: flex;
  align-items: center;
}

/* Input de búsqueda */
.cc-search-input {
  width: 100%;
  padding: 0 14px;
  border: 1px solid #e0e0e0;
  @include typography-small;
  border-radius: $radius-lg;
  transition: all 0.2s ease;
  background-color: var(--vt-c-white-soft);
  height: 38px;
  box-sizing: border-box;
  line-height: 38px;
}

.cc-search-input:focus {
  outline: none;
  border-color: var(--vt-c-teal-dark);
  box-shadow: 0 0 0 3px rgba(var(--vt-c-teal-dark), 0.1);
  background-color: var(--vt-c-white);
}

/* Estilos del DropDownList */
:deep(.k-dropdown) {
  width: 100%;
  border-radius: $radius-lg;
  border-color: #e0e0e0;
  background-color: var(--vt-c-white-soft);
}

:deep(.k-dropdown:focus) {
  box-shadow: 0 0 0 3px rgba(var(--vt-c-teal-dark), 0.1);
  border-color: var(--vt-c-teal-dark);
}

:deep(.k-dropdown .k-input-inner) {
  padding: 0 14px;
  color: #333;
  height: 38px;
  box-sizing: border-box;
  line-height: 38px;
}

/* Estilos específicos para el dropdown personalizado */
.cc-dropdown {
  height: 38px !important;
}

:deep(.cc-dropdown .k-input-inner),
:deep(.cc-dropdown .k-input) {
  height: 38px !important;
  line-height: 38px !important;
  padding: 0 14px !important;
  box-sizing: border-box !important;
}

:deep(.cc-dropdown .k-select) {
  height: 38px !important;
  line-height: 38px !important;
}

/* Lista de documentos */
.cc-documents-list {
  min-height: 200px;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  border-radius: $radius-lg;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  box-sizing: border-box;
}

/* Mensaje de carga y sin resultados */
.cc-loading,
.cc-no-results {
  text-align: center;
  padding: $spacing-10 $spacing-5;
  color: #666;
  background-color: var(--vt-c-white-soft);
  border-radius: $radius-sm;
}

.cc-no-results {
  font-style: italic;
}

/* Grupo de año */
.cc-year-group {
  width: 100%;
  max-width: 100%;
}

/* Encabezado del año */
.cc-year-header {
  @include typography-small;
  @include font-weight-semibold;
  margin: 0;
  padding: $spacing-3 $spacing-5;
  background-color: #e9f5f7;
  color: var(--vt-c-teal-dark);
  text-align: left;
  width: 100%;
  border-bottom: 1px solid #e0e0e0;
  border-top: 1px solid #e0e0e0;
}

.cc-year-group:first-child .cc-year-header {
  border-top: none;
}

/* Fila de documento */
.cc-document-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $spacing-4 $spacing-5;
  border-bottom: 1px solid #e0e0e0;
  transition: all 0.2s ease;
  width: 100%;
  background-color: var(--vt-c-white);
}

.cc-document-row:hover {
  background-color: #fefefe;
}

.cc-document-row:last-child {
  border-bottom: none;
}

/* Información del documento */
.cc-document-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Fecha del documento */
.cc-document-date {
  @include typography-caption;
  @include font-weight-medium;
  color: #666;
  margin-bottom: 4px;
}

/* Nombre del documento */
.cc-document-name {
  @include typography-small;
  @include font-weight-medium;
  color: #333;
}

/* Acciones del documento */
.cc-document-actions {
  display: flex;
  align-items: center;
}

/* Botón de vista */
.cc-view-button {
  background-color: var(--vt-c-teal-dark);
  color: white;
  border: none;
  padding: $spacing-2 $spacing-4;
  @include typography-caption;
  @include font-weight-medium;
  border-radius: $radius-sm;
  transition: all 0.2s ease;
  cursor: pointer;
}

.cc-view-button:hover {
  background-color: #134249;
  box-shadow: 0 2px 5px rgba(black, 0.1);
}

/* Estilos responsive */
@media (max-width: 767px) {
  .cc-controls-row {
    flex-direction: column;
    gap: $spacing-4;
  }

  .cc-filter-container {
    width: 100%;
    min-width: 100%;
  }

  .cc-document-row {
    flex-direction: column;
    align-items: flex-start;
    gap: $spacing-3;
    padding: $spacing-4 $spacing-5;
  }

  .cc-document-actions {
    align-self: flex-start;
    width: 100%;
  }

  .cc-view-button {
    width: 100%;
    text-align: center;
    padding: $spacing-2-5 $spacing-4;
  }

  .cc-header {
    flex-direction: column;
    align-items: flex-start;
    gap: $spacing-2-5;
    padding: $spacing-4;
  }

  .cc-document-date {
    @include typography-caption;
  }

  .cc-document-name {
    @include typography-small;
  }
}
</style>
